import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Chip, Dialog, Select, TextField } from '@nyce/admin-kit/ui';
import {
  ActionsRow,
  ConfirmButton,
  EmptyState,
  ErrorBox,
  ListField,
  Loading,
  Page,
  TableScroller,
  Tabs,
  useAsync,
  useNotice,
} from '../../../components/common';
import { saveBlob, useBuddyWrite } from '../../api/client';
import { useOperatorNames } from '../../../operators';
import type {
  AuthTenant,
  ConnectionTestResult,
  GlobalSettings,
  LineFlavor,
  Organization,
  ProvisionItemState,
  ProvisionReport,
  ProvisionRequest,
  SchemaState,
  System,
  SystemInput,
  Tenant,
} from '../../api/setup';
import { CustomerDocuments } from '../knowledge/CustomerDocuments';
import { SkillToggles } from '../knowledge/SkillToggles';
import { FeatureFlagsEditor, ModelSettingsEditor, flagOn } from './settings';
import {
  AuthTenantLinks,
  SwitchRow,
  UsageLink,
  messageOf,
  useQueryParam,
  useQueryPatch,
  useSetup,
} from './shared';

type Tab = 'general' | 'access' | 'features' | 'settings' | 'skills' | 'documents';
const TABS: { value: Tab; label: string }[] = [
  { value: 'general', label: 'General' },
  { value: 'access', label: 'Access' },
  { value: 'features', label: 'Features' },
  { value: 'settings', label: 'Settings' },
  { value: 'skills', label: 'Skills' },
  { value: 'documents', label: 'Documents' },
];

const empty = (tenantId = ''): SystemInput => ({
  id: '',
  name: '',
  tenantId,
  authTenantId: tenantId,
  organizationId: null,
  allowedGroups: ['NYCEBUDDY'],
  adminGroups: [],
  hostnamePatterns: [],
  allowedOrigins: [],
  features: {},
  settings: {},
  enabled: true,
});

const toInput = (s: System): SystemInput => ({
  id: s.id,
  name: s.name,
  tenantId: s.tenantId,
  authTenantId: s.authTenantId || s.tenantId,
  organizationId: s.organizationId ?? null,
  allowedGroups: [...(s.allowedGroups ?? [])],
  adminGroups: [...(s.adminGroups ?? [])],
  hostnamePatterns: [...(s.hostnamePatterns ?? [])],
  allowedOrigins: [...(s.allowedOrigins ?? [])],
  features: { ...(s.features ?? {}) },
  settings: { ...(s.settings ?? {}) },
  enabled: s.enabled,
});

/** A system carries no secrets (they are the tenant's), so a copy is complete but for the id. */
const copyOf = (s: System): SystemInput => ({ ...toInput(s), id: '', name: `${s.name} (copy)` });

const host = (u?: string) => {
  try {
    return new URL(/^[a-z]+:\/\//i.test(u ?? '') ? u! : `http://${u ?? ''}`).host.toLowerCase();
  } catch {
    return '';
  }
};
const sameHost = (a?: string, b?: string) => !!a && !!b && host(a) === host(b);

function ConnectionResult({ test }: { test: ConnectionTestResult }) {
  const parts = [
    test.workserver &&
      `workserver ${test.workserver.ok ? `${test.workserver.version ?? 'ok'}${test.workserver.ms != null ? ` (${test.workserver.ms} ms)` : ''}` : (test.workserver.error ?? 'failed')}`,
    test.auth && `AUTH ${test.auth.ok ? 'ok' : (test.auth.error ?? 'failed')}`,
    test.businessinfo &&
      `businessinfo ${test.businessinfo.ok ? `${test.businessinfo.entities ?? '?'} entities` : (test.businessinfo.error ?? 'failed')}`,
  ].filter(Boolean);
  return (
    <div
      className={`bd-result ${test.ok ? 'bd-result-ok' : 'bd-result-fail'}`}
      data-testid="connection-result"
    >
      <b>{test.ok ? 'Connection OK' : `Connection failed${test.error ? `: ${test.error}` : ''}`}</b>
      {parts.length > 0 && <div>{parts.join(' · ')}</div>}
    </div>
  );
}

function SystemEditor({
  initial,
  isNew,
  copiedFrom,
  tenants,
  authTenants,
  organizations,
  global,
  canWrite,
  onSave,
  onClose,
}: {
  initial: SystemInput;
  isNew: boolean;
  copiedFrom?: string;
  tenants: Tenant[];
  authTenants: AuthTenant[];
  organizations: Organization[];
  global: GlobalSettings | null;
  canWrite: boolean;
  onSave: (v: SystemInput) => Promise<void>;
  onClose: () => void;
}) {
  const { target, api } = useSetup();
  const [v, setV] = useState(initial);
  const [tabRaw, setTab] = useQueryParam('tab');
  const tab = (TABS.some((t) => t.value === tabRaw) ? tabRaw : 'general') as Tab;
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'save' | 'test' | null>(null);
  const [test, setTest] = useState<ConnectionTestResult | null>(null);
  const ro = !canWrite;
  const set = <K extends keyof SystemInput>(k: K, value: SystemInput[K]) =>
    setV((cur) => ({ ...cur, [k]: value }));
  const tenant = tenants.find((t) => t.id === v.tenantId);
  const organization = organizations.find((o) => o.id === v.organizationId);
  // Both cascades: global → tenant → organization (it hangs off this system; the tenant may serve another) → system.
  const inherited = {
    ...(global?.settings ?? {}),
    ...(tenant?.settings ?? {}),
    ...(organization?.settings ?? {}),
  };
  const inheritedFeatures = {
    ...(global?.features ?? {}),
    ...(tenant?.features ?? {}),
    ...(organization?.features ?? {}),
  };
  // AUTH binds one identity provider (the workserver) per tenant: a mismatch is called out instead of failing at sign-in.
  const suggested = authTenants.find((t) => sameHost(t.identityProviderUrl, tenant?.baseUrl));
  const authName = new Map(authTenants.map((t) => [t.id, t]));
  const tenantOptions = tenants.map((t) => ({
    value: t.id,
    label: `${t.name}${t.baseUrl ? ` — ${host(t.baseUrl)}` : ''}`,
    group: authName.get(t.id)?.customerName || undefined,
  }));
  if (v.tenantId && !tenants.some((t) => t.id === v.tenantId))
    tenantOptions.push({ value: v.tenantId, label: `${v.tenantId} (unknown)`, group: undefined });
  const save = async () => {
    setBusy('save');
    setError(null);
    try {
      await onSave({ ...v, id: v.id.trim(), name: v.name.trim() });
      onClose();
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(null);
    }
  };
  const runTest = async () => {
    setBusy('test');
    setTest(null);
    try {
      setTest(await api.systems.testConnection(v.id, v));
    } catch (e) {
      setTest({ ok: false, error: messageOf(e) });
    } finally {
      setBusy(null);
    }
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title={
        copiedFrom ? `New system (copy of ${copiedFrom})` : isNew ? 'New system' : `System ${v.name || v.id}`
      }
      maxWidth={780}
      footer={
        <>
          {!isNew && canWrite && (
            <Button variant="secondary" onClick={() => void runTest()} loading={busy === 'test'}>
              Test connection
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>
            {ro ? 'Close' : 'Cancel'}
          </Button>
          {!ro && (
            <Button
              onClick={() => void save()}
              loading={busy === 'save'}
              disabled={!v.id.trim() || !v.name.trim() || !v.tenantId}
            >
              Save
            </Button>
          )}
        </>
      }
    >
      {copiedFrom && (
        <p className="nyk-notice nyk-notice-warn" data-testid="copy-note">
          Copied from <b>{copiedFrom}</b>: same installation, groups and overrides. Give it its own id and,
          for a second registration of the workserver (the customer’s and our support staff’s), its own
          organization — that separates the budget, the statistics and the conversations.
        </p>
      )}
      <ErrorBox error={error} />
      {test && <ConnectionResult test={test} />}
      <Tabs value={tab} onChange={setTab} options={TABS} />
      {tab === 'general' && (
        <div className="nyk-form">
          <TextField
            label="Id"
            value={v.id}
            onChange={(e) => set('id', e.target.value)}
            disabled={!isNew || ro}
            hint={isNew ? 'slug; cannot be changed later' : 'cannot be changed'}
            required
          />
          <TextField
            label="Name"
            value={v.name}
            onChange={(e) => set('name', e.target.value)}
            disabled={ro}
            required
          />
          <Select
            label="Tenant (installation)"
            value={v.tenantId}
            onChange={(id) => setV((cur) => ({ ...cur, tenantId: id, authTenantId: id }))}
            options={tenantOptions}
            placeholder="Select the tenant…"
            disabled={ro}
          />
          <Select
            label="Organization"
            value={v.organizationId ?? ''}
            onChange={(id) => set('organizationId', id || null)}
            options={[
              { value: '', label: '— none —' },
              ...organizations.map((o) => ({ value: o.id, label: `${o.name} (${o.id})` })),
            ]}
            hint="the budget and the statistics roll up here"
            disabled={ro}
          />
          {suggested && suggested.id !== v.tenantId && (
            <div className="nyk-notice nyk-notice-warn nyk-span2" data-testid="tenant-mismatch">
              AUTH authenticates this workserver under <b>{suggested.name || suggested.id}</b> (
              <code>{suggested.id}</code>); another tenant here makes every call answer 403{' '}
              <code>foreign_system</code>.{' '}
              {!ro && tenants.some((t) => t.id === suggested.id) && (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() =>
                    setV((cur) => ({ ...cur, tenantId: suggested.id, authTenantId: suggested.id }))
                  }
                >
                  Use it
                </Button>
              )}
            </div>
          )}
          <p className="nyk-ui-hint nyk-span2">
            The workserver, service account, AUTH service key, log source and database belong to the tenant
            (Tenants page).{tenant?.baseUrl ? ` This one answers for ${tenant.baseUrl}.` : ''}
          </p>
          <SwitchRow
            label={v.enabled ? 'Enabled: users can ask' : 'Disabled: sign-in and chat refused'}
            checked={v.enabled}
            onChange={(enabled) => set('enabled', enabled)}
            disabled={ro}
          />
        </div>
      )}
      {tab === 'access' && (
        <div className="nyk-form">
          <ListField
            label="Allowed groups"
            value={v.allowedGroups}
            onChange={(x) => set('allowedGroups', x)}
            disabled={ro}
            hint="NYCE.LOGIC groups that may ask (comma separated; default NYCEBUDDY)"
          />
          <ListField
            label="Admin groups"
            value={v.adminGroups}
            onChange={(x) => set('adminGroups', x)}
            disabled={ro}
            hint="system admins in the chat (feedback, customer documents)"
          />
          <div className="nyk-span2">
            <ListField
              label="URI patterns (login picker)"
              value={v.hostnamePatterns}
              onChange={(x) => set('hostnamePatterns', x)}
              disabled={ro}
              hint="A hostname (buddy.acme.com, *.acme.com) lists this system on that host; a pattern with a path (*/stark) publishes it at that address only. Empty = listed everywhere."
            />
          </div>
          <div className="nyk-span2">
            <ListField
              label="Allowed origins (CORS for the embedded panel)"
              value={v.allowedOrigins}
              onChange={(x) => set('allowedOrigins', x)}
              disabled={ro}
              placeholder="https://buddy.acme.com, *.acme.com"
              hint="A whole origin matches scheme, host and port; a bare host either scheme; *.acme.com the subdomains only. Every host here may call the API from a browser."
            />
          </div>
        </div>
      )}
      {tab === 'features' && (
        <FeatureFlagsEditor
          value={v.features}
          onChange={(features) => set('features', features)}
          inherited={inheritedFeatures}
          disabled={ro}
        />
      )}
      {tab === 'settings' && (
        <ModelSettingsEditor
          value={v.settings}
          onChange={(settings) => set('settings', settings)}
          inherited={inherited}
          disabled={ro}
        />
      )}
      {(tab === 'skills' || tab === 'documents') && isNew && <EmptyState title="Save the system first." />}
      {tab === 'skills' && !isNew && <SkillToggles target={target} scope="system" scopeId={v.id} />}
      {tab === 'documents' && !isNew && (
        <>
          {!flagOn({ ...inheritedFeatures, ...v.features }, 'customerDocs') && (
            <p className="nyk-notice nyk-notice-warn bd-tab-hint">
              The Customer documents feature is off for this system: the chat does not search these documents
              until it is switched on (Features).
            </p>
          )}
          <CustomerDocuments target={target} systemId={v.id} />
        </>
      )}
    </Dialog>
  );
}

const STATE_LABEL: Record<ProvisionItemState, string> = {
  missing: 'will be created',
  outdated: 'will be replaced',
  current: 'already in place',
  unknown: 'unknown',
  created: 'created',
  updated: 'replaced',
  unchanged: 'unchanged',
  skipped: 'skipped',
  failed: 'failed',
};
const STATE_TONE: Partial<Record<ProvisionItemState, string>> = {
  missing: 'nyk-tone-blue',
  outdated: 'nyk-tone-amber',
  current: 'nyk-tone-grey',
  created: 'nyk-tone-green',
  updated: 'nyk-tone-green',
  unchanged: 'nyk-tone-grey',
  failed: 'nyk-tone-red',
};

function ProvisionItems({ report }: { report: ProvisionReport }) {
  return (
    <TableScroller>
      <table className="nyk-ui-table nyk-cards" data-testid="provision-items">
        <thead>
          <tr>
            <th>Object</th>
            <th>State</th>
          </tr>
        </thead>
        <tbody>
          {report.items.map((i) => (
            <tr key={i.key} data-provision-item={i.key} data-state={i.state}>
              <td className="nyk-card-title">
                <b>{i.label}</b>
                {i.detail && <div className="nyk-ui-hint">{i.detail}</div>}
              </td>
              <td data-label="State">
                <Chip size="sm" className={STATE_TONE[i.state]}>
                  {STATE_LABEL[i.state] ?? i.state}
                </Chip>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableScroller>
  );
}

/**
 * Preview, then apply (behind a confirm): what a provisioning run writes into the installation's NYCE.LOGIC database
 * (buddy3 `docs/design/provisioning.md`), the SQL for a DBA, and the earlier runs. Read-only operators see the runs and
 * may download the SQL.
 */
function ProvisionDialog({
  system,
  canWrite,
  onClose,
}: {
  system: System;
  canWrite: boolean;
  onClose: () => void;
}) {
  const { api } = useSetup();
  const [request, setRequest] = useState<ProvisionRequest>({ workflow: true, groups: true, launch: false });
  const [flavor, setFlavor] = useState<'' | LineFlavor>('');
  const [preview, setPreview] = useState<ProvisionReport | null>(null);
  const [applied, setApplied] = useState<ProvisionReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'preview' | 'apply' | 'sql' | null>(null);
  const runs = useAsync(() => api.systems.provisionRuns(system.id), [api, system.id]);
  // buddy3 keeps who ran it as the token's `sub`: a WorkServer user code, or the AUTH user id of a hub operator.
  const names = useOperatorNames(runs.data?.map((r) => r.user));
  const body = (): ProvisionRequest => ({ ...request, flavor: flavor || undefined });
  const configured = !!system.dbServerId && !!system.dbName;
  const nothing = !request.workflow && !request.groups && !request.launch;

  const run = async (kind: 'preview' | 'apply') => {
    setBusy(kind);
    setError(null);
    try {
      const r =
        kind === 'preview'
          ? await api.systems.provisionPreview(system.id, body())
          : await api.systems.provision(system.id, body());
      if (kind === 'preview') {
        setPreview(r);
        setApplied(null);
      } else {
        setApplied(r);
        void runs.reload();
      }
      if (!r.ok) setError(r.error ?? 'The run failed; nothing was written.');
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(null);
    }
  };
  const sql = async () => {
    setBusy('sql');
    setError(null);
    try {
      const { blob, fileName } = await api.systems.provisioningSql(system.id);
      saveBlob(blob, fileName);
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(null);
    }
  };
  const shown = applied ?? preview;
  // Changing the request invalidates the preview: apply only what was previewed.
  const change = (patch: ProvisionRequest) => {
    setRequest((cur) => ({ ...cur, ...patch }));
    setPreview(null);
  };
  const toggle = (key: 'workflow' | 'groups' | 'launch', label: string) => (
    <SwitchRow
      label={label}
      checked={!!request[key]}
      onChange={(on) => change({ [key]: on })}
      disabled={!canWrite}
    />
  );

  return (
    <Dialog
      open
      onClose={onClose}
      title={`Provision ${system.name || system.id}`}
      maxWidth={780}
      footer={
        <>
          <Button variant="ghost" onClick={() => void sql()} loading={busy === 'sql'}>
            Download SQL
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          {canWrite && configured && (
            <>
              <Button
                variant="secondary"
                onClick={() => void run('preview')}
                loading={busy === 'preview'}
                disabled={nothing || busy !== null}
              >
                Preview
              </Button>
              <ConfirmButton
                message={`Write the selected objects into ${system.dbName} (one transaction)? The workserver must be restarted afterwards.`}
                onConfirm={() => void run('apply')}
                loading={busy === 'apply'}
                disabled={nothing || busy !== null || !preview?.ok || !!applied}
              >
                Apply
              </ConfirmButton>
            </>
          )}
        </>
      }
    >
      <ErrorBox error={error} />
      {!configured ? (
        <p className="nyk-card-intro">
          The tenant of this system has no database server and database yet: set them on the Tenants page, or
          download the SQL for a DBA.
        </p>
      ) : (
        <p className="nyk-card-intro">
          Writes into <code>{system.dbName}</code> on database server <code>{system.dbServerId}</code> in one
          transaction. Every statement is repeatable: what is already in place is left alone. Preview first;
          Apply writes exactly the previewed selection.
        </p>
      )}
      {canWrite && configured && (
        <div className="nyk-form">
          {toggle('workflow', 'nyceBuddy v3 workflow line (CUSTOM_API)')}
          {toggle('groups', 'NYCEBUDDY* groups and NYCEBUDDY_SVC')}
          {toggle('launch', 'START_BUDDY and the ME_NYCEBUDDY menu element')}
          <Select
            label="Server release"
            value={flavor}
            onChange={(f) => {
              setFlavor(f as '' | LineFlavor);
              setPreview(null);
            }}
            options={[
              { value: '', label: 'Detect from the installation' },
              { value: 'async', label: '2026.9 and later (async mediator)' },
              { value: 'legacy', label: '2026.6 – 2026.8 (sync mediator, auth service)' },
              { value: 'classic', label: 'Before 2026.6 (sync mediator, no auth service)' },
            ]}
          />
        </div>
      )}
      {shown && (
        <div className="bd-runs" data-testid={applied ? 'provision-applied' : 'provision-preview'}>
          <p className="nyk-ui-hint">
            {applied ? 'Applied to' : 'Preview of'} {shown.database} on {shown.host}
            {shown.databaseVersion ? ` — ${shown.databaseVersion}` : ''} — line flavor <b>{shown.flavor}</b>
            {shown.flavorSource ? ` (${shown.flavorSource})` : ''}
          </p>
          {!shown.applied && !flavor && shown.flavorSource?.startsWith('not detected') && (
            <p className="nyk-notice nyk-notice-warn">
              Pick the server release before applying: the installation did not answer bot/health (normal
              before its first line), and the wrong release compiles to nothing.
            </p>
          )}
          <ProvisionItems report={shown} />
          {applied?.restartRequired && (
            <p className="nyk-notice nyk-notice-warn" data-testid="restart-required">
              Restart the NYCE.LOGIC workserver: it compiles CUSTOM_API lines only at start-up. Compile errors
              show in its log.
            </p>
          )}
          {applied?.ok && !applied.restartRequired && (
            <p className="nyk-notice">Everything was already in place; nothing was written.</p>
          )}
        </div>
      )}
      <div className="bd-runs">
        <h3 className="nyk-ui-eyebrow">Earlier runs</h3>
        <ErrorBox error={runs.error} />
        {runs.data && !runs.data.length && <p className="nyk-ui-hint">No runs yet.</p>}
        {!!runs.data?.length && (
          <TableScroller>
            <table className="nyk-ui-table nyk-cards" data-testid="provision-runs">
              <thead>
                <tr>
                  <th>When</th>
                  <th>By</th>
                  <th>Objects</th>
                  <th>Flavor</th>
                  <th>Result</th>
                </tr>
              </thead>
              <tbody>
                {runs.data.map((r) => (
                  <tr key={r.id} data-run={r.id}>
                    <td className="nyk-card-title">{new Date(r.at).toLocaleString()}</td>
                    <td data-label="By" title={names[r.user] ? r.user : undefined}>
                      {names[r.user] ?? r.user}
                    </td>
                    <td data-label="Objects">
                      {(['workflow', 'groups', 'launch'] as const).filter((k) => r.objects?.[k]).join(', ') ||
                        '—'}
                    </td>
                    <td data-label="Flavor">{r.flavor}</td>
                    <td data-label="Result">
                      <Chip size="sm" className={r.ok ? 'nyk-tone-green' : 'nyk-tone-red'}>
                        {r.ok ? 'ok' : 'failed'}
                      </Chip>
                      {r.error && <div className="nyk-ui-hint">{r.error}</div>}
                      {r.result?.restartRequired && <div className="nyk-ui-hint">restart required</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroller>
        )}
      </div>
    </Dialog>
  );
}

function SchemaCell({ schema }: { schema?: SchemaState }) {
  if (schema?.refreshing) return <Chip size="sm">refreshing…</Chip>;
  if (schema?.cachedAt)
    return (
      <span title={schema.hash}>
        {new Date(schema.cachedAt).toLocaleString()}
        <div className="nyk-ui-hint">
          {schema.entities ?? '?'} entities{schema.source ? ` · ${schema.source}` : ''}
        </div>
        {schema.error && <div className="nyk-text-red">{schema.error}</div>}
      </span>
    );
  return <span className="nyk-ui-hint">{schema?.error ?? 'not cached'}</span>;
}

function StatusCell({ s }: { s: System }) {
  const state = !s.enabled ? 'disabled' : s.available === false ? 'unavailable' : 'ok';
  return (
    <>
      <Chip
        size="sm"
        className={
          state === 'ok' ? 'nyk-tone-green' : state === 'unavailable' ? 'nyk-tone-red' : 'nyk-tone-grey'
        }
      >
        {state}
      </Chip>
      {s.error && <div className="nyk-ui-hint">{s.error}</div>}
    </>
  );
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** Systems = registrations of an installation for one organization: access, features, settings, schema, provisioning. */
export function BuddySystemsPage() {
  const { target, api } = useSetup();
  const canWrite = useBuddyWrite();
  const systems = useAsync(() => api.systems.list(), [api]);
  const tenants = useAsync(() => api.tenants.list(), [api]);
  const organizations = useAsync(() => api.organizations.list().catch(() => [] as Organization[]), [api]);
  const global = useAsync(() => api.globalSettings.get().catch(() => null), [api]);
  // Best effort: without AUTH access the editor falls back to the tenants buddy3 stores.
  const authTenants = useAsync(
    () =>
      canWrite ? api.authTenants().catch(() => [] as AuthTenant[]) : Promise.resolve([] as AuthTenant[]),
    [api, canWrite]
  );
  const [edit] = useQueryParam('edit');
  const [copyFrom] = useQueryParam('copy');
  const [provisionId, setProvisionId] = useQueryParam('provision');
  const [q, setQ] = useQueryParam('q');
  const patch = useQueryPatch();
  const toast = useNotice();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [schemas, setSchemas] = useState<Record<string, SchemaState>>({});
  const alive = useRef(true);
  useEffect(
    () => () => {
      alive.current = false;
    },
    []
  );
  const open = (id: string, copy?: string) => patch({ edit: id, copy: copy ?? null, tab: null });
  const close = () => patch({ edit: null, copy: null, tab: null });

  const list = useMemo(() => systems.data ?? [], [systems.data]);
  const isNew = edit === 'new' && canWrite;
  const source = isNew && copyFrom ? list.find((s) => s.id === copyFrom) : undefined;
  const current = isNew ? null : list.find((s) => s.id === edit);
  const initial = isNew
    ? source
      ? copyOf(source)
      : empty(tenants.data?.[0]?.id)
    : current
      ? toInput(current)
      : null;
  const provisioning = list.find((s) => s.id === provisionId);

  // Grouped by organization (budgets), not by tenant: two registrations of one workserver share a tenant.
  const groups = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const rows = list.filter(
      (s) =>
        !needle ||
        [s.id, s.name, s.tenantId, s.tenantName, s.organizationId, s.organizationName, s.baseUrl].some((x) =>
          x?.toLowerCase().includes(needle)
        )
    );
    const map = new Map<string, System[]>();
    for (const s of rows) map.set(s.organizationId ?? '', [...(map.get(s.organizationId ?? '') ?? []), s]);
    const nameOf = (id: string, items: System[]) =>
      items[0].organizationName ??
      organizations.data?.find((o) => o.id === id)?.name ??
      (id || 'No organization');
    return [...map.entries()]
      .map(([id, items]) => ({ id, name: nameOf(id, items), items }))
      .sort((a, b) =>
        !a.id ? 1 : !b.id ? -1 : a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
      );
  }, [list, organizations.data, q]);

  const save = async (v: SystemInput) => {
    if (isNew) await api.systems.create(v);
    else await api.systems.update(v.id, v);
    toast.success(`Saved ${v.name}`);
    await systems.reload();
  };
  const action = async (s: System, fn: () => Promise<unknown>, done?: string) => {
    setBusyId(s.id);
    toast.clear();
    try {
      await fn();
      await systems.reload();
      if (done) toast.success(done);
    } catch (e) {
      toast.error(`${s.name}: ${messageOf(e)}`);
    } finally {
      setBusyId(null);
    }
  };
  /** Refresh is asynchronous: poll the schema state until it settles (at most a minute). */
  const refreshSchema = async (s: System) => {
    setBusyId(s.id);
    toast.clear();
    try {
      let state = await api.systems.refreshSchema(s.id);
      setSchemas((m) => ({ ...m, [s.id]: { ...state, refreshing: true } }));
      for (let i = 0; i < 30 && alive.current; i++) {
        state = await api.systems.schema(s.id);
        if (!alive.current) return;
        setSchemas((m) => ({ ...m, [s.id]: state }));
        if (!state.refreshing) break;
        await sleep(2000);
      }
      if (state.error) toast.error(`${s.name}: ${state.error}`);
      else if (!state.refreshing)
        toast.success(`Schema of ${s.name} refreshed (${state.entities ?? '?'} entities)`);
    } catch (e) {
      toast.error(`${s.name}: ${messageOf(e)}`);
    } finally {
      if (alive.current) setBusyId(null);
    }
  };

  return (
    <Page
      title="Systems"
      description="Registrations of an installation (tenant) for one organization: access groups, features, engine settings, schema and provisioning."
      actions={
        <>
          <input
            className="nyk-ui-input nyk-search"
            type="search"
            aria-label="Filter systems"
            placeholder="Filter system, tenant or organization"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          {canWrite && <Button onClick={() => open('new')}>New system</Button>}
        </>
      }
    >
      <ErrorBox error={systems.error ?? tenants.error} />
      {toast.element}
      <Loading loading={systems.loading && !systems.data} />
      {systems.data && !list.length && (
        <EmptyState
          card
          title="No systems yet."
          hint="Create the tenant (installation) first, then register it here for an organization."
        />
      )}
      {!!list.length && !groups.length && <EmptyState card title={`Nothing matches “${q}”`} />}
      {groups.map((g) => (
        <section key={g.id} className="nyk-card" data-organization={g.id}>
          <div className="nyk-card-head">
            <div className="nyk-card-title-group">
              <h2>{g.name}</h2>
              {g.id && <span className="nyk-ui-hint">{g.id}</span>}
            </div>
            {g.id && <UsageLink kind="organization" id={g.id} target={target} />}
          </div>
          <TableScroller>
            <table className="nyk-ui-table nyk-cards">
              <thead>
                <tr>
                  <th>System</th>
                  <th>Tenant</th>
                  <th>Schema</th>
                  <th>Status</th>
                </tr>
              </thead>
              {g.items.map((s) => (
                <tbody key={s.id} className="nyk-row-group" data-system={s.id}>
                  <tr>
                    <td className="nyk-card-title">
                      <b>{s.name}</b>
                      <div className="nyk-ui-hint">{s.id}</div>
                    </td>
                    <td data-label="Tenant">
                      {/* One block: on a phone the card cell lays its children out in a row. */}
                      <div>
                        {s.tenantName ?? s.tenantId}
                        {s.environment && (
                          <>
                            {' '}
                            <Chip size="sm">{s.environment}</Chip>
                          </>
                        )}
                        {s.baseUrl && <div className="nyk-ui-hint nyk-url">{s.baseUrl}</div>}
                        <AuthTenantLinks tenantId={s.authTenantId || s.tenantId} target={target} />
                      </div>
                    </td>
                    <td data-label="Schema" data-schema={s.id}>
                      <SchemaCell schema={schemas[s.id] ?? s.schema} />
                    </td>
                    <td data-label="Status">
                      <StatusCell s={s} />
                    </td>
                  </tr>
                  <ActionsRow colSpan={4}>
                    <UsageLink kind="system" id={s.id} target={target} />
                    <Button size="sm" variant="ghost" onClick={() => open(s.id)}>
                      {canWrite ? 'Edit' : 'View'}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setProvisionId(s.id)}>
                      Provisioning
                    </Button>
                    {canWrite && (
                      <>
                        <Button
                          size="sm"
                          variant="ghost"
                          loading={busyId === s.id}
                          onClick={() => void refreshSchema(s)}
                        >
                          Refresh schema
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => open('new', s.id)}>
                          Copy
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={busyId === s.id}
                          onClick={() =>
                            void action(
                              s,
                              () => api.systems.update(s.id, { enabled: !s.enabled }),
                              `${s.enabled ? 'Disabled' : 'Enabled'} ${s.name}`
                            )
                          }
                        >
                          {s.enabled ? 'Disable' : 'Enable'}
                        </Button>
                        <ConfirmButton
                          size="sm"
                          variant="danger"
                          message={`Delete system ${s.name}?`}
                          onConfirm={() =>
                            void action(s, () => api.systems.remove(s.id), `Deleted ${s.name}`)
                          }
                        >
                          Delete
                        </ConfirmButton>
                      </>
                    )}
                  </ActionsRow>
                </tbody>
              ))}
            </table>
          </TableScroller>
        </section>
      ))}
      {initial && (!isNew || !copyFrom || source || systems.data) && (
        <SystemEditor
          key={`${edit}:${copyFrom}`}
          initial={initial}
          isNew={isNew}
          copiedFrom={source?.name}
          tenants={tenants.data ?? []}
          authTenants={authTenants.data ?? []}
          organizations={organizations.data ?? []}
          global={global.data ?? null}
          canWrite={canWrite}
          onSave={save}
          onClose={close}
        />
      )}
      {provisioning && (
        <ProvisionDialog
          key={provisioning.id}
          system={provisioning}
          canWrite={canWrite}
          onClose={() => setProvisionId(null)}
        />
      )}
    </Page>
  );
}
